import React, {
  useCallback,
  useMemo,
  useState,
} from "react";

import {
  Box,
  Button,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from "@material-ui/core";

import StepConnector from "@material-ui/core/StepConnector";
import { makeStyles } from "@material-ui/core/styles";
import PropTypes from "prop-types";
import { useStoreState } from "easy-peasy";

const COMPLETED_COLOR = "#2ca66f";
const INACTIVE_COLOR = "#d9d9d9";
const DEFAULT_PRIMARY_COLOR = "#4f8bc9";

const stepInfo = [
  {
    title: "Step 1 of 3: Create Username",
    description: "Choose a unique username to get started",
  },
  {
    title: "Step 2 of 3: Create Password",
    description: "Choose a strong password",
  },
];

const useStyles = makeStyles({
  root: {
    width: "100%",
  },

  stepper: {
    width: "100%",
    maxWidth: 520,
    margin: "0 auto",
    padding: 0,
    backgroundColor: "transparent",
  },

  connectorAlternativeLabel: {
    top: 11,
    left: "calc(-50% + 14px)",
    right: "calc(50% + 14px)",
  },

  connectorLine: {
    borderTopWidth: 1,
    borderColor: INACTIVE_COLOR,
  },

  connectorActive: {
    "& $connectorLine": {
      borderColor: (props) => props.primaryColor,
    },
  },

  connectorCompleted: {
    "& $connectorLine": {
      borderColor: COMPLETED_COLOR,
    },
  },

  labelContainer: {
    marginTop: 5,
  },

  stepTitle: {
    display: "block",
    color: "#222",
    fontSize: 14,
    fontWeight: 600,
    lineHeight: 1.25,
  },

  activeStepTitle: {
    color: (props) => props.primaryColor,
  },

  stepDescription: {
    display: "block",
    maxWidth: 115,
    margin: "3px auto 0",
    color: "#999",
    fontSize: 10,
    fontWeight: 400,
    lineHeight: 1.25,
  },

  content: {
    width: "100%",
    maxWidth: (props) => props.isLastStep ? 700 : 405,
    margin: "0 auto 0",
  },

  actions: {
    width: "100%",
    maxWidth: 405,
    margin: "20px auto 0",
    display: "flex",
    justifyContent: "flex-start",
  },

  buttonRow: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },

  button: {
    minWidth: 70,
    minHeight: 32,
    padding: "5px 14px",
    borderRadius: 4,
    fontSize: 11,
    fontWeight: 500,
    lineHeight: 1.2,
    textTransform: "none",
  },

  cancelButton: {
    backgroundColor: "#fff",
    color: "#555",
    border: "1px solid #bfc3c7",
    boxShadow: "none",

    "&:hover": {
      backgroundColor: "#f7f7f7",
      boxShadow: "none",
    },
  },

  primaryButton: {
    backgroundColor: (props) => props.primaryColor,
    color: "#fff",

    "&:hover": {
      backgroundColor: (props) => props.primaryColor,
      opacity: 0.9,
    },
  },

  disabledButton: {
    backgroundColor: "#b0b0b0",
    color: "#f5f5f5",
  },
});

function CustomStepIcon(props) {
  const {
    active,
    completed,
    icon,
  } = props;

  const brandSettings = useStoreState(
    (state) => state.siteSettingsModel.brandSettings
  );

  const primaryColor =
    brandSettings?.Secondary ||
    DEFAULT_PRIMARY_COLOR;

  let backgroundColor = INACTIVE_COLOR;

  if (completed) {
    backgroundColor = COMPLETED_COLOR;
  } else if (active) {
    backgroundColor = primaryColor;
  }

  return (
    <div
      style={{
        width: 22,
        height: 22,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor,
        color: "#fff",
        fontSize: 10,
        fontWeight: 700,
        boxSizing: "border-box",
      }}
    >
      {completed ? (
        <span
          style={{
            display: "block",
            fontSize: 14,
            lineHeight: 1,
            transform: "translateY(-1px)",
          }}
        >
          ✓
        </span>
      ) : (
        icon
      )}
    </div>
  );
}

CustomStepIcon.propTypes = {
  active: PropTypes.bool,
  completed: PropTypes.bool,
  icon: PropTypes.node,
};

function normalizeSteps(steps) {
  if (Array.isArray(steps)) {
    return steps.map((step, index) => {
      if (
        step &&
        typeof step === "object" &&
        !React.isValidElement(step)
      ) {
        return {
          label:
            step.label ||
            `Step ${index + 1}`,

          description:
            step.description || "",
        };
      }

      return {
        label:
          step ||
          `Step ${index + 1}`,

        description: step.description,
      };
    });
  }

  const stepCount = Math.max(
    1,
    Number(steps) || 1
  );

  return Array.from(
    { length: stepCount },
    (_, index) => ({
      label: `Step ${index + 1}`,
      description: "",
    })
  );
}

function NewStepper({
  steps,
  renderStepContent,

  nextLabel = "Next",
  finishLabel = "Finish",
  cancelLabel = "Cancel",
  completeRegistrationLabel = "Complete Registration",

  validateStep,
  beforeNext,
  beforeFinish,

  onCancel,
  onFinish,
  onStepChange,

  getActionsForStep,
  isNextDisabled,
  isCompleteDisabled,

  initialStep = 0,

  className,
  styles = {},

  stepperProps = {},
  nextButtonProps = {},
  cancelButtonProps = {},
  finishButtonProps = {},

  agreeCallback,
}) {
  const brandSettings = useStoreState(
    (state) => state.siteSettingsModel.brandSettings
  );

  const [activeStep, setActiveStep] =
    useState(initialStep);

  const [pending, setPending] =
    useState(false);

  const [completeRegistration, setCompleteRegistration] =
    useState(false);

  const stepsArray = useMemo(
    () => normalizeSteps(steps),
    [steps]
  );

  const isLastStep =
    activeStep === stepsArray.length - 1;

  const primaryColor =
    brandSettings?.Secondary ||
    DEFAULT_PRIMARY_COLOR;

  const classes = useStyles({
    primaryColor,
    isLastStep,
  });

  const goToStep = useCallback(
    (nextStep) => {
      const safeStep = Math.max(
        0,
        Math.min(
          nextStep,
          stepsArray.length - 1
        )
      );

      setActiveStep(safeStep);

      if (
        typeof onStepChange === "function"
      ) {
        onStepChange(safeStep);
      }
    },
    [
      onStepChange,
      stepsArray.length,
    ]
  );

  const runCheck = useCallback(
    async (actionType) => {
      setPending(true);

      try {
        if (
          typeof validateStep ===
          "function"
        ) {
          const isValid =
            await validateStep(activeStep);

          if (!isValid) {
            return false;
          }
        }

        if (
          actionType === "next" &&
          typeof beforeNext === "function"
        ) {
          const canContinue =
            await beforeNext(activeStep);

          if (!canContinue) {
            return false;
          }
        }

        if (
          actionType === "finish" &&
          typeof beforeFinish === "function"
        ) {
          const canFinish =
            await beforeFinish(activeStep);

          if (!canFinish) {
            return false;
          }
        }

        return true;
      } catch (error) {
        console.error(
          "Stepper validation failed:",
          error
        );

        return false;
      } finally {
        setPending(false);
      }
    },
    [
      activeStep,
      validateStep,
      beforeNext,
      beforeFinish,
    ]
  );

  const handleNext = useCallback(
    async () => {
      if (isLastStep) {
        return;
      }

      const canContinue =
        await runCheck("next");

      if (!canContinue) {
        return;
      }

      goToStep(activeStep + 1);
    },
    [
      activeStep,
      goToStep,
      isLastStep,
      runCheck,
    ]
  );

  const handleFinish = useCallback(
    async () => {
      const canFinish =
        await runCheck("finish");

      if (!canFinish) {
        return;
      }

      if (
        typeof onFinish === "function"
      ) {
        await onFinish(activeStep);
      }
    },
    [
      activeStep,
      onFinish,
      runCheck,
    ]
  );

  const handleAgree = useCallback(
    async () => {
      const actionType = isLastStep
        ? "finish"
        : "next";

      const canContinue =
        await runCheck(actionType);

      if (!canContinue) {
        return;
      }

      if (
        typeof agreeCallback ===
        "function"
      ) {
        setCompleteRegistration(true);
        await agreeCallback(activeStep);
      }

      if (isLastStep) {
        if (
          typeof onFinish ===
          "function"
        ) {
          await onFinish(activeStep);
        }

        return;
      }

      goToStep(activeStep + 1);
    },
    [
      activeStep,
      agreeCallback,
      goToStep,
      isLastStep,
      onFinish,
      runCheck,
    ]
  );

  const perStepNextLabel =
    Array.isArray(nextLabel)
      ? nextLabel[activeStep]
      : null;

  const nextButtonText =
    perStepNextLabel ||
    (typeof nextLabel === "string"
      ? nextLabel
      : "Next");

  const completeButtonText =
    perStepNextLabel ||
    (typeof nextLabel === "string"
      ? completeRegistrationLabel
      : "Complete Registration");

  const actions =
    typeof getActionsForStep ===
      "function"
      ? getActionsForStep(activeStep)
      : isLastStep
        ? ["cancel", "finish"]
        : ["cancel", "next"];

  const nextDisabled =
    pending ||
    (
      typeof isNextDisabled ===
      "function" &&
      isNextDisabled(activeStep)
    );

  const completeDisabled =
    pending ||
    (
      typeof isCompleteDisabled ===
      "function" &&
      isCompleteDisabled(activeStep)
    );

  return (
    <>
      <div className='d-flex flex-column justify-content-center align-items-center mb-4'>
        <h2 style={{ color: brandSettings.Secondary }}>{isLastStep ? 'You have successfully created an Account!' : 'Create an Account now before your card arrives'}</h2>
        {!isLastStep && <span>Set up your online Account now to track the arrival of your card</span>}
      </div>
      <Box
        className={`${classes.root} ${className || ""
          }`}
        style={styles.container}
      >
        <Stepper
          activeStep={activeStep}
          alternativeLabel
          connector={
            <StepConnector
              classes={{
                alternativeLabel:
                  classes.connectorAlternativeLabel,

                active:
                  classes.connectorActive,

                completed:
                  classes.connectorCompleted,

                line:
                  classes.connectorLine,
              }}
            />
          }
          classes={{
            root: classes.stepper,
          }}
          {...stepperProps}
        >
          {stepsArray.map(
            (step, index) => (
              <Step
                key={`${index}-${String(
                  step.label
                )}`}
                completed={
                  index < activeStep
                }
              >
                <StepLabel
                  StepIconComponent={
                    CustomStepIcon
                  }
                  classes={{
                    labelContainer:
                      classes.labelContainer,
                  }}
                >
                  <Typography
                    component="span"
                    className={`${classes.stepTitle
                      } ${index === activeStep
                        ? classes.activeStepTitle
                        : ""
                      }`}
                  >
                    {step.label}
                  </Typography>

                  {step.description ? (
                    <Typography
                      component="span"
                      className={
                        classes.stepDescription
                      }
                    >
                      {step.description}
                    </Typography>
                  ) : null}
                </StepLabel>
              </Step>
            )
          )}
        </Stepper>

        {
          activeStep < 2 && (
            <Box
              style={{
                maxWidth: 405,
                margin: "28px auto 0",
              }}
            >
              <Typography
                style={{
                  fontSize: 16,
                  fontWeight: 600,
                  color: "#222",
                  marginBottom: 2,
                }}
              >
                {stepInfo[activeStep].title}
              </Typography>

              <Typography
                style={{
                  fontSize: 12,
                  color: "#8a8a8a",
                }}
              >
                {stepInfo[activeStep].description}
              </Typography>
            </Box>
          )
        }
        <Box
          className={classes.content}
          style={styles.content}
        >
          {typeof renderStepContent ===
            "function" ? (
            renderStepContent(activeStep)
          ) : (
            <Typography
              variant="body2"
              color="textSecondary"
            />
          )}
        </Box>

        {Array.isArray(actions) &&
          actions.length > 0 ? (
          <Box
            className={classes.actions}
            style={styles.actions}
          >
            <div
              className={
                classes.buttonRow
              }
            >
              {actions.includes(
                "cancel"
              ) ? (
                <Button
                  variant="contained"
                  onClick={onCancel}
                  disabled={pending}
                  className={`${classes.button} ${classes.cancelButton}`}
                  style={
                    styles.buttonCancel
                  }
                  {...cancelButtonProps}
                >
                  {cancelLabel}
                </Button>
              ) : null}

              {actions.includes(
                "next"
              ) ? (
                <Button
                  variant="contained"
                  onClick={handleNext}
                  disabled={nextDisabled}
                  className={`${classes.button} ${nextDisabled
                    ? classes.disabledButton
                    : classes.primaryButton
                    }`}
                  style={
                    styles.buttonNext
                  }
                  {...nextButtonProps}
                >
                  {pending
                    ? "..."
                    : nextButtonText + " >"}
                </Button>
              ) : null}

              {actions.includes(
                "complete registration"
              ) ? (
                <Button
                  variant="contained"
                  onClick={handleAgree}
                  disabled={completeDisabled || completeRegistration}
                  className={`${classes.button} ${completeDisabled
                    ? classes.disabledButton
                    : classes.primaryButton
                    }`}
                  style={
                    styles.buttonConfirm
                  }
                  {...nextButtonProps}
                >
                  {pending
                    ? "..."
                    : completeButtonText}
                </Button>
              ) : null}

              {actions.includes(
                "finish"
              ) ? (
                <Button
                  variant="contained"
                  onClick={handleFinish}
                  disabled={pending}
                  className={`${classes.button} ${classes.primaryButton}`}
                  style={
                    styles.buttonFinish ||
                    styles.buttonNext
                  }
                  {...finishButtonProps}
                >
                  {pending
                    ? "..."
                    : perStepNextLabel ||
                    finishLabel}
                </Button>
              ) : null}
            </div>
          </Box>
        ) : null}
      </Box>
    </>

  );
}

NewStepper.propTypes = {
  steps: PropTypes.oneOfType([
    PropTypes.number,

    PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.node,

        PropTypes.shape({
          label:
            PropTypes.node.isRequired,

          description:
            PropTypes.node,
        }),
      ])
    ),
  ]).isRequired,

  renderStepContent: PropTypes.func,

  nextLabel: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(
      PropTypes.string
    ),
  ]),

  finishLabel: PropTypes.string,
  cancelLabel: PropTypes.string,
  agreeLabel: PropTypes.string,

  validateStep: PropTypes.func,
  beforeNext: PropTypes.func,
  beforeFinish: PropTypes.func,

  onCancel: PropTypes.func,
  onFinish: PropTypes.func,
  onStepChange: PropTypes.func,

  getActionsForStep:
    PropTypes.func,

  isNextDisabled:
    PropTypes.func,

  initialStep: PropTypes.number,

  className: PropTypes.string,
  styles: PropTypes.object,

  stepperProps:
    PropTypes.object,

  nextButtonProps:
    PropTypes.object,

  cancelButtonProps:
    PropTypes.object,

  finishButtonProps:
    PropTypes.object,

  agreeButtonProps:
    PropTypes.object,

  agreeCallback:
    PropTypes.func,
};

export default NewStepper;